import { createContext, ReactNode, useContext } from 'react';

import { DropEvent, DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';

import { mergeClasses } from '@/utils';

import { Attachment01Icon } from '@/assets/icons';
import { Button } from '@/components/common';

import { RENDER_BYTES } from '@/constants';

interface IDropzoneContext {
  src?: File[];
  accept?: DropzoneOptions['accept'];
  maxSize?: DropzoneOptions['maxSize'];
  minSize?: DropzoneOptions['minSize'];
  maxFiles?: DropzoneOptions['maxFiles'];
}

interface IDropzone extends Omit<DropzoneOptions, 'onDrop'> {
  src?: File[];
  className?: string;
  onDrop?: (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => void;
  children?: ReactNode;
  error?: string;
  variant?: 'default' | 'compact';
}

const DropzoneContext = createContext<IDropzoneContext | undefined>(undefined);

const getDropzoneCaptions = (
  accept?: DropzoneOptions['accept'],
  maxSize?: DropzoneOptions['maxSize'],
  minSize?: DropzoneOptions['minSize'],
  customTypeMessage?: string,
  customSizeMessage?: string,
): { captionType: string; captionSize: string } => {
  let captionType = '';
  let captionSize = '';

  if (accept) {
    const extensions = [...new Set(Object.values(accept).flat())];
    if (customTypeMessage) {
      captionType = customTypeMessage;
    } else {
      captionType += extensions.length > 1 ? 'Allowed file types: ' : 'You can only upload ';
      captionType += extensions.join(', ');
      captionType += extensions.length === 1 ? ' files.' : '';
    }
  }

  if (customSizeMessage) {
    captionSize = customSizeMessage;
  } else {
    if (minSize && maxSize) {
      captionSize += `File size between ${RENDER_BYTES(minSize)} and ${RENDER_BYTES(maxSize)}`;
    } else if (minSize) {
      captionSize += `Files can't be smaller than ${RENDER_BYTES(minSize)}.`;
    } else if (maxSize) {
      captionSize += `Files can't be larger than ${RENDER_BYTES(maxSize)}.`;
    }
  }

  return { captionType, captionSize };
};

const Dropzone = ({
  accept,
  maxFiles = 1,
  maxSize,
  minSize,
  onDrop,
  onError,
  disabled,
  src,
  className,
  children,
  error,
  variant = 'default',
  ...props
}: IDropzone) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    onError,
    disabled,
    onDrop: (acceptedFiles, fileRejections, event) => {
      if (fileRejections.length > 0) {
        const message = fileRejections.at(0)?.errors.at(0)?.message;
        onError?.(new Error(message));
        return;
      }
      onDrop?.(acceptedFiles, fileRejections, event);
    },
    ...props,
  });

  const { captionType, captionSize } = getDropzoneCaptions(accept, maxSize, minSize);

  return (
    <DropzoneContext.Provider
      key={JSON.stringify(src)}
      value={{ src, accept, maxSize, minSize, maxFiles }}
    >
      <div
        className={mergeClasses('flex flex-col gap-4 w-full', variant === 'compact' && '!gap-2')}
      >
        <Button
          className={mergeClasses(
            'relative h-auto w-full flex-col overflow-hidden !p-9 rounded-small !shadow-none',
            isDragActive
              ? 'outline-none !border-primary-base !bg-primary-lightest'
              : '!border-neutral-4 dark:!border-neutral-9',
            error && '!border-semantic-error-base',
            variant === 'compact' && '!px-4 !py-3',
            className,
          )}
          disabled={disabled}
          type="button"
          buttonStyle="outline"
          buttonColor="neutral"
          {...getRootProps()}
        >
          <input
            {...getInputProps()}
            disabled={disabled}
            onKeyDown={(e) => {
              if (disabled) e.preventDefault();
            }}
          />
          {children}
        </Button>
        <div className="flex flex-col">
          {captionType && captionSize && variant === 'compact' && (
            <p className="text-wrap text-body-sm font-medium text-neutral-7">
              {captionType} {captionSize}
            </p>
          )}
          {error && (
            <p className="text-xs leading-5 font-medium text-semantic-error-base">{error}</p>
          )}
        </div>
      </div>
    </DropzoneContext.Provider>
  );
};

const DropzoneEmptyState = ({
  className,
  customTypeMessage,
  customSizeMessage,
  variant = 'default',
}: {
  className?: string;
  customTypeMessage?: string;
  customSizeMessage?: string;
  variant?: 'default' | 'compact';
}) => {
  const context = useContext(DropzoneContext);
  if (!context) {
    throw new Error('useDropzoneContext must be used within a Dropzone');
  }
  const { accept, maxSize, minSize } = context;
  const { captionType, captionSize } = getDropzoneCaptions(
    accept,
    maxSize,
    minSize,
    customTypeMessage,
    customSizeMessage,
  );

  return (
    <div
      className={mergeClasses(
        'flex p-0',
        variant === 'compact'
          ? 'w-full flex-row justify-start items-center gap-2'
          : 'flex-col items-center justify-center gap-4',
        className,
      )}
    >
      <div className="flex items-center justify-center rounded-small bg-transparent">
        <Attachment01Icon
          className={mergeClasses('size-8 text-neutral-6', variant === 'compact' && '!size-6')}
        />
      </div>
      <div
        className={mergeClasses(
          'w-full truncate text-wrap text-base font-medium text-neutral-7 dark:text-neutral-6',
          variant === 'compact' && 'text-start',
        )}
      >
        Drag and drop your file here or{' '}
        <p className="inline text-primary-base font-semibold underline">Browse</p>
      </div>
      {variant !== 'compact' && (
        <div className="text-neutral-6 dark:text-neutral-7">
          {captionType && <p className="text-wrap text-body-sm font-medium">{captionType}</p>}
          {captionSize && <p className="text-wrap text-body-sm font-medium">{captionSize}</p>}
        </div>
      )}
    </div>
  );
};

export { Dropzone, DropzoneEmptyState };
